import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReviewStars } from "./ReviewStars";

interface ProductCardReviewsProps {
  variantId: string;
}

type Aggregate = { avg: number; count: number };

let cache: Map<string, Aggregate> | null = null;
let inflight: Promise<Map<string, Aggregate>> | null = null;
const listeners = new Set<() => void>();

const loadAll = (): Promise<Map<string, Aggregate>> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase
      .from("reviews")
      .select("variant_id,rating")
      .eq("approved", true);
    {
      const map = new Map<string, { sum: number; count: number }>();
      (data ?? []).forEach((r: any) => {
        const cur = map.get(r.variant_id) ?? { sum: 0, count: 0 };
        cur.sum += r.rating;
        cur.count += 1;
        map.set(r.variant_id, cur);
      });
      const result = new Map<string, Aggregate>();
      map.forEach((v, k) => result.set(k, { avg: v.sum / v.count, count: v.count }));
      cache = result;
      inflight = null;
      listeners.forEach((l) => l());
      return result;
    }
  })();
  return inflight;
};

export const ProductCardReviews = ({ variantId }: ProductCardReviewsProps) => {
  const [agg, setAgg] = useState<Aggregate | null>(() => cache?.get(variantId) ?? null);

  useEffect(() => {
    let active = true;
    const update = () => {
      if (!active) return;
      const a = cache?.get(variantId);
      if (a) setAgg(a);
      else setAgg({ avg: 0, count: 0 });
    };
    if (cache) update();
    else {
      listeners.add(update);
      loadAll().then(update);
    }
    return () => {
      active = false;
      listeners.delete(update);
    };
  }, [variantId]);

  if (!agg || agg.count === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <ReviewStars rating={Math.round(agg.avg)} size={12} />
      <span className="text-xs text-muted-foreground">({agg.count})</span>
    </div>
  );
};
